import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, Loader2 } from "lucide-react";
import { SettingsMetaPage } from "./SettingsMetaPageLayout";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";

interface MetaAdAccount {
  id: string;
  name?: string;
}

export function SettingsMetaBusinessAdAccountsRoute() {
  const { businessId } = useParams<{ businessId: string }>();
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const [isLoading, setIsLoading] = useState(true);
  const [adAccounts, setAdAccounts] = useState<MetaAdAccount[]>([]);

  useEffect(() => {
    async function fetchAdAccounts() {
      if (!businessId) return;

      setIsLoading(true);
      try {
        const [businessResponse, adAccountsResponse] = await Promise.all([
          fetch(`/api/meta/businesses/${businessId}`),
          fetch(`/api/meta/businesses/${businessId}/owned_ad_accounts?fields=name`),
        ]);

        const businessName =
          businessResponse.ok
            ? ((await businessResponse.json()) as { business?: { name?: string } }).business
                ?.name || businessId
            : businessId;

        setBreadcrumbs([
          { label: "Settings", path: "/settings" },
          { label: "Integrations", path: "/settings/integrations" },
          { label: "Meta", path: "/settings/integrations/meta" },
          { label: businessName, path: `/settings/meta/businesses/${businessId}` },
          {
            label: "Ad Accounts",
            path: `/settings/meta/businesses/${businessId}/ad-accounts`,
          },
        ]);

        if (adAccountsResponse.ok) {
          const data = (await adAccountsResponse.json()) as { adAccounts?: MetaAdAccount[] };
          setAdAccounts(data.adAccounts ?? []);
        } else {
          console.error("Failed to fetch Meta ad accounts");
        }
      } catch (error) {
        console.error("Failed to fetch Meta ad accounts:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchAdAccounts();

    return () => {
      clearBreadcrumbs();
    };
  }, [businessId, setBreadcrumbs, clearBreadcrumbs]);

  if (isLoading) {
    return (
      <SettingsMetaPage className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </SettingsMetaPage>
    );
  }

  return (
    <SettingsMetaPage>
      {adAccounts.length === 0 ? (
        <p className="text-sm text-muted-foreground">No ad accounts found.</p>
      ) : (
        adAccounts.map((adAccount) => (
          <Link
            key={adAccount.id}
            to={`/settings/meta/businesses/${businessId}/ad-accounts/${adAccount.id}`}
            className="flex items-center justify-between border rounded-lg p-4 hover:bg-muted/50 transition-colors"
          >
            <span className="font-medium">{adAccount.name || adAccount.id}</span>
            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
          </Link>
        ))
      )}
    </SettingsMetaPage>
  );
}
